import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePanelState as useBasePanelState } from '../../../shared/components/panel';

export type PotDetailsPanelType =
  | 'approve'
  | 'reject'
  | 'rejectionManagement'
  | 'timeline'
  | 'markPaid'
  | 'adminCancel'
  // New panel types for detail drawers
  | 'siteDetails'
  | 'plotDetails'
  | 'labourItemDetails'
  | 'variationDetails'
  | 'allocationDetails'
  | 'deductionDetails'
  | 'userDetails';

/**
 * Pot details page specific version of usePanelState
 * Extends the base panel state for pot detail panels with additional context params
 *
 * @example
 * ```tsx
 * const { isOpen, type, id, siteId, plotId, openPanel, closePanel } = usePotDetailsPanelState();
 *
 * // Open approve pot panel
 * openPanel('approve', potId);
 *
 * // Open site details panel
 * openPanel('siteDetails', siteId.toString());
 *
 * // Open plot details panel with site context
 * openPanel('plotDetails', plotId.toString(), { siteId: siteId.toString() });
 *
 * // Open user details panel
 * openPanel('userDetails', userId);
 * ```
 */
export function usePotDetailsPanelState() {
  const base = useBasePanelState();
  const [searchParams] = useSearchParams();

  // Extract additional context params from URL
  const siteId = searchParams.get('siteId');
  const plotId = searchParams.get('plotId');

  // Extended panel opener with optional additional params
  const openPanel = useCallback(
    (
      type: PotDetailsPanelType,
      id: string,
      additionalParams?: Record<string, string>
    ) => {
      base.openPanel(type, id, additionalParams);
    },
    [base]
  );

  return {
    ...base,
    type: base.type as PotDetailsPanelType | null,
    siteId,
    plotId,
    openPanel,
  };
}
